import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer, useTransition, useDeferredValue, lazy, Suspense } from 'react';
import { apiGet } from '../../utils/api';
// Removed framer-motion to avoid mount-time animation overhead
import VirtualizedMessageList from './components/VirtualizedMessageList';
import offlineStorage from '../../utils/offlineStorage';
import { useWebWorker } from './hooks/useWebWorker';
import chatCache from '../../utils/cache';
import useOfflineStatus from './hooks/useOfflineStatus';
import useAuthStore from '../../store/authStore';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { db, storage } from '../../firebase';
// ⚡️ SECURITY: DOMPurify moved to Web Worker for better performance
import CryptoJS from 'crypto-js';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  addDoc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  getDocs,
  limit,
  startAfter,
  deleteDoc,
  arrayUnion,
  where,
  limitToLast
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { FaTrash, FaPaperclip, FaTimes, FaRegSmile } from 'react-icons/fa';
import useUserOnlineStatus from './hooks/useUserOnlineStatus';
import { useSocket } from './hooks/useSocket';
import { toast } from 'react-toastify';
import LazyImage from '../LazyImage';
import useLazyMessages from './hooks/useLazyMessages';

import MessageBubble from './components/MessageBubble';
import InputArea from './components/InputArea';

// ⚡️ BOX COMPONENTS: Import extracted box components
import MarketplaceHeader from './components/boxes/MarketplaceHeader';
import EscrowWorksBox from './components/boxes/EscrowWorksBox';
import TransactionStatusBox from './components/boxes/TransactionStatusBox';
import OrderDetailsCard from './components/boxes/OrderDetailsCard';
import LoginDetailsBox from './components/boxes/LoginDetailsBox';

// ⚡️ UTILITY FUNCTIONS: Import from extracted utility files
import { 
  ALLOWED_FILE_TYPES, 
  MAX_FILE_SIZE, 
  MAX_MESSAGE_LENGTH, 
  RATE_LIMIT_WINDOW, 
  MAX_MESSAGES_PER_MINUTE,
  tsToMs, 
  uniqueByKey, 
  validateFile, 
  validateInput, 
  validateEmoji, 
  logSecurityEvent, 
  getStatusBadgeClass,
  getTextContent,
  isNear,
  formatFileSize,
  getFileExtension,
  isImageFile,
  isVideoFile
} from './utils/chatUtils';
import { 
  validateMessageType, 
  verifyMessageIntegrity, 
  validateSocketMessage, 
  sanitizeMessageContent, 
  validateMessageBeforeSend, 
  validateFileMessage, 
  validateOrderMessage 
} from './utils/messageValidation';
import { useChatState } from './hooks/useChatState';
import { useMessageProcessing } from './hooks/useMessageProcessing';
import { useFirebaseChat } from './hooks/useFirebaseChat';
import { useSocketChat } from './hooks/useSocketChat';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useFileHandling } from './hooks/useFileHandling';
import ChatHeader from './components/ChatHeader';
import OfflineIndicator from './components/OfflineIndicator';
import DragDropOverlay from './components/DragDropOverlay';

// ⚡️ MESSAGE COMPONENTS: Import extracted message components
import MessageList from './components/messages/MessageList';
import MessageStatus from './components/messages/MessageStatus';
import OrderMessage from './components/messages/OrderMessage';

// ⚡️ MODAL COMPONENTS: Import extracted modal components
import ImagePreviewModal from './components/modals/ImagePreviewModal';

// ⚡️ PERFORMANCE HOOKS: Import extracted performance hooks
import usePerformanceMonitoring from './hooks/usePerformanceMonitoring';
import useMessageOptimization from './hooks/useMessageOptimization';

// ⚡️ CONTEXT MENU HOOK: Import extracted context menu hook
import useContextMenu from './hooks/useContextMenu';
import MessageContextMenu from './components/MessageContextMenu';
import useAgentConversationListener from './hooks/useAgentConversationListener';
import useBuyerSellerConversationListener from './hooks/useBuyerSellerConversationListener';
import useTypingIndicator from './hooks/useTypingIndicator';

// ⚡️ BUNDLE OPTIMIZATION: Lazy load heavy components
const ProductModal = lazy(() => import('./components/ProductModal'));

// ⚡️ TIMESTAMP NORMALIZATION: Now imported from ./chat/utils/chatUtils.js

// 🔧 FINAL DEDUPLICATION: Now imported from ./chat/utils/chatUtils.js

// Security: Enhanced file type validation constants - Now imported from ./chat/utils/chatUtils.js

// 🛡️ REMOVED CLIENT-SIDE RATE LIMITING - Now handled server-side
// Security: Enhanced file validation - Now imported from ./chat/utils/chatUtils.js

// ⚡️ SECURITY: Use Web Worker for text sanitization (moved to worker)

// 🛡️ REMOVED CLIENT-SIDE RATE LIMITING - Now handled server-side via API endpoints

// Security: Input validation function - Now imported from ./chat/utils/chatUtils.js

// Security: Emoji validation - Now imported from ./chat/utils/chatUtils.js

// 🛡️ REMOVED CLIENT-SIDE ENCRYPTION - Now handled server-side for security
// 🛡️ REMOVED ENVIRONMENT VARIABLE EXPOSURE - Security keys moved to server

// Security: Audit logging - Now imported from ./chat/utils/chatUtils.js

// 🛡️ REMOVED CLIENT-SIDE CSRF TOKEN - Now handled server-side for security

// ⚡️ PERFORMANCE OPTIMIZATION: State reducer - Now imported from ./chat/hooks/useChatState.js

// Security: Enhanced message type validation - Now imported from ./chat/utils/messageValidation.js

// Security: Enhanced message integrity verification - Now imported from ./chat/utils/messageValidation.js

// ⚡️ SECURITY: Socket message validation moved inside component for Web Worker access


// Status badge class function - Now imported from ./chat/utils/chatUtils.js

export default function ChatPanel({ conversation, activeTab, moveConversationToTop, onSelectConversation, transactions = [], agentMessages = [], setAgentMessages, updateAgentChatPreview, updateMessagesPreview, messages, setMessages, isAdminDashboard, firestoreConvId, selectedConversationId }) {
  const { isDarkMode } = useDarkMode();
  const { user } = useAuthStore();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted state management hook
  const { state, dispatch, ...stateHelpers } = useChatState();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted message processing hook
  const messageProcessing = useMessageProcessing();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted Firebase operations hook
  const firebaseChat = useFirebaseChat();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted Socket operations hook
  const socketChat = useSocketChat();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted scroll management hook
  const scrollManagement = useScrollManagement();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted file handling hook
  const fileHandling = useFileHandling();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted performance monitoring hook
  const performanceMonitoring = usePerformanceMonitoring();
  
  // Extract functions for dependency arrays
  const { deduplicateMessage, validateSocketMessage, getDeduplicationStats } = messageProcessing;
  
  // 🚀 REACT 18 CONCURRENCY: Prevent UI blocking during heavy updates
  const [isPending, startTransition] = useTransition();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Web Worker for CPU-heavy tasks
  const { sanitizeText, encryptText: encryptTextWithWorker, decryptText: decryptTextWithWorker, processMessages: processMessagesWithWorker, validateFile: validateFileWithWorker, isWorkerAvailable } = useWebWorker();
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication - Now imported from ./chat/hooks/useMessageProcessing.js
  
  // Destructure state for easier access
  const {
    input, showEmojiPicker, sending, loadingMore, showTick, firestoreLoading,
    uploadingFile, uploading, uploadProgress, uploadedFileUrl, openedImage,
    readMessages, messageStatus, showProductModal,
    isDragOver, pendingMessages, lastVisible, hasMore, firestoreMessages,
    localFirestoreConvId
  } = state;
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted message optimization hook (after state destructuring)
  const messageOptimization = useMessageOptimization(firestoreMessages, pendingMessages, conversation, messageProcessing);
  
  const emojiPickerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { joinRoom, sendMessage, onMessage, isConnected, getConnectionStatus } = useSocket({ enabled: true });
  const { isOnline, offlineQueue, addToOfflineQueue, getOfflineStatus } = useOfflineStatus();

  // 🎨 REPLY FUNCTIONALITY: State for reply-to message
  const [replyToMessage, setReplyToMessage] = useState(null);

  // 🚀 PRODUCTION SECURITY: Environment sanity check on component mount
  useEffect(() => {
    // 🛡️ REMOVED CLIENT-SIDE ENVIRONMENT CHECKS - Now handled server-side
  }, []);

  // 🚀 AVATAR PRELOADING: Preload next conversation's avatar for smooth UX
  useEffect(() => {
    if (conversation?.avatar) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = conversation.avatar;
      link.imagesrcset = conversation.avatar;
      document.head.appendChild(link);
      
      return () => {
        document.head.removeChild(link);
      };
    }
  }, [conversation?.avatar]);

  // 🚀 BACKGROUND SYNC: Register for offline message retries
  useEffect(() => {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then(registration => {
        registration.sync.register('send-messages').catch(error => {
        });
      });
    }
  }, []);

  // 1. Compute variables like otherPartyIdentifier before calling hooks.
  const otherPartyIdentifier = useMemo(() => {
    if (isAdminDashboard && conversation && (conversation.uid || conversation.userUid)) {
      // For admin conversations, try to get both UID and email
      const uid = conversation.uid || conversation.userUid;
      const email = conversation.userEmail || conversation.email;
      return { uid, email };
    } else if (user && conversation && conversation.buyer && conversation.seller) {
      // For buyer/seller conversations, get the other party's info
      const isBuyer = conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase();
      const otherParty = isBuyer ? conversation.seller : conversation.buyer;
      return {
        uid: otherParty.uid || otherParty.id,
        email: otherParty.email
      };
    }
    return null;
  }, [isAdminDashboard, conversation, user]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Use extracted message optimization hook
  const {
    currentMessages,
    sortedAndFilteredMessages,
    pendingMessagesForDisplay,
    allVisibleMessages
  } = messageOptimization;

  // ⚡️ PERFORMANCE OPTIMIZATION: Disabled lazy loading to prevent space box issues
  // const { visibleMessages, hasMore: hasMoreLazy, isLoading: loadingMoreLazy, handleScroll } = useLazyMessages(sortedAndFilteredMessages, 30);
  const visibleMessages = sortedAndFilteredMessages;
  const hasMoreLazy = false;
  const loadingMoreLazy = false;
  const handleScroll = () => {}; // No-op function

  useEffect(() => {
    // Only mark as read if this chat panel is for the currently selected conversation
    if (
      !user ||
      !conversation ||
      !firestoreMessages.length ||
      (typeof selectedConversationId !== 'undefined' && conversation.id !== selectedConversationId)
    ) {
      return;
    }
    const unreadMessages = firestoreMessages.filter(msg =>
      !msg.readBy?.includes(user.email) &&
      msg.from !== user.email
    );
    const convId = conversation?.id || firestoreConvId || localFirestoreConvId;
    if (unreadMessages.length > 0 && convId) {
      // Mark messages as read in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < unreadMessages.length; i += batchSize) {
        const batch = unreadMessages.slice(i, i + batchSize);
        Promise.all(batch.map(msg => firebaseChat.markMessageAsRead(msg.id, convId, user, conversation, isAdminDashboard, dispatch)))
          .then(() => {})
          .catch(error => {});
      }
    }
  }, [firestoreMessages, user, conversation, firestoreConvId, localFirestoreConvId, selectedConversationId]);

  // Add this function for marking messages as read
  // const markMessageAsRead = async (messageId, conversationId) => { ... } // Moved to useFirebaseChat.js

  // Add this function to update message status when sending
  // const updateMessageStatus = async (messageId, conversationId, status) => { ... } // Moved to useFirebaseChat.js

  // Update the useEffect that marks messages as read so it triggers whenever the chat panel is open and new messages arrive
  useEffect(() => {
    // Only mark as read if this chat panel is for the currently selected conversation
    if (
      !user ||
      !conversation ||
      !firestoreMessages.length ||
      (typeof selectedConversationId !== 'undefined' && conversation.id !== selectedConversationId)
    ) {
      return;
    }

    const unreadMessages = firestoreMessages.filter(msg =>
      !msg.readBy?.includes(user.email) &&
      msg.from !== user.email
    );

    const convId = conversation?.id || firestoreConvId || localFirestoreConvId;
    if (unreadMessages.length > 0 && convId) {
      // Mark messages as read in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < unreadMessages.length; i += batchSize) {
        const batch = unreadMessages.slice(i, i + batchSize);
        Promise.all(batch.map(msg => firebaseChat.markMessageAsRead(msg.id, convId, user, conversation, isAdminDashboard, dispatch)))
          .then(() => {
          })
          .catch(error => {
          });
      }
    }
  }, [firestoreMessages, user, conversation, firestoreConvId, localFirestoreConvId, selectedConversationId]);

  // --- ONLINE STATUS HOOK FIX ---
  const { online: otherPartyOnline, lastSeen: otherPartyLastSeen } = useUserOnlineStatus(otherPartyIdentifier);

  // Firestore: Paginated message loading
  const PAGE_SIZE = 20;

  // 🔥 CREATE AGENT CHAT ROOM: Now using extracted Firebase hook
  // const createAgentChatRoom = async (convId, user) => { ... } // Moved to useFirebaseChat.js

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced loadMessages with dual-cache system
  // const loadMessages = async (convId, opts = {}) => { ... } // Moved to useFirebaseChat.js

  // const loadMessagesFromServer = async (convId, opts = {}) => { ... } // Moved to useFirebaseChat.js

  // ⚡️ PERFORMANCE OPTIMIZATION: Combined Firestore and socket room management
  useEffect(() => {
    if (!user || !conversation) return;
    
    let convId = null;
    if (firestoreConvId) {
      convId = firestoreConvId;
    } else if (conversation.id === 'admin') {
      convId = `agent_${user.uid || user.id || user.email}`;
      
      // 🔥 CREATE AGENT CHAT ROOM: Automatically create Firebase chat room for agent conversations
      firebaseChat.createAgentChatRoom(convId, user, dispatch);
    }
    if (!convId) return;

    dispatch({ type: 'SET_LOCAL_FIRESTORE_CONV_ID', payload: convId });
    
    // Load initial messages without loading state
    firebaseChat.loadMessages(convId, {}, dispatch, firestoreMessages);
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Join socket room immediately after conversation setup
    if (isConnected && conversation && conversation.id) {
      joinRoom(conversation.id);
    }
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Clear message deduplication cache when switching conversations
    messageProcessing.seenMessageIds.current.clear();
    
  }, [user, conversation, firestoreConvId, isConnected, joinRoom]);

  // 🔌 Firestore listeners via hooks
  useAgentConversationListener({
    db,
    user,
    conversation,
    firestoreConvId,
    localFirestoreConvId,
    dispatch,
    chatCache,
    offlineStorage
  });

  useBuyerSellerConversationListener({
    db,
    conversation,
    user,
    dispatch,
    offlineStorage,
    pendingMessages
  });

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced scroll handler for lazy loading
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    
    const handleScrollEvent = () => {
      // Handle lazy loading of older messages
      handleScroll(container);
      
      // ⚡️ OPTIMIZATION: Load older messages when user scrolls to top
      if (container.scrollTop === 0 && hasMore && !loadingMore) {
        // For agent conversations
        if (firestoreConvId || localFirestoreConvId) {
          // ✅ FIX: Only load more if lastVisible has a valid timestamp
          if (lastVisible && lastVisible.data) {
            const lastVisibleData = lastVisible.data();
            if (lastVisibleData.timestamp && typeof lastVisibleData.timestamp.toDate === 'function') {
              firebaseChat.loadMessages(firestoreConvId || localFirestoreConvId, { startAfterDoc: lastVisible, append: true }, dispatch, firestoreMessages);
            }
          }
        }
        // For regular conversations - implement similar logic if needed
        // This would require tracking the oldest message for regular conversations
      }
      
      // ✅ FIX: Enhanced scroll position tracking
      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 100;
      const nearBottom = scrollHeight - scrollTop - clientHeight < threshold;
      
      // Track if user manually scrolled up
      if (!nearBottom && isNearBottom) {
        userScrolledUpRef.current = true;
        setShouldAutoScroll(false);
      } else if (nearBottom && userScrolledUpRef.current) {
        userScrolledUpRef.current = false;
        setShouldAutoScroll(true);
      }
      
      setIsNearBottom(nearBottom);
      
      // Also handle position tracking for auto-scroll
      scrollManagement.handleScrollPosition(chatContainerRef, {
        setIsNearBottom,
        setShouldAutoScroll,
        shouldAutoScroll
      });
    };
    
    container.addEventListener('scroll', handleScrollEvent);
    return () => container.removeEventListener('scroll', handleScrollEvent);
  }, [hasMore, loadingMore, firestoreConvId, localFirestoreConvId, lastVisible]);

  // 🔥 AGENT CONVERSATION LISTENER: Real-time updates for agent chat rooms
  useEffect(() => {
    if (!user || !conversation || conversation.id !== 'admin') return;
    
    const convId = firestoreConvId || localFirestoreConvId;
    if (!convId) return;
    
    
    // Set up real-time listener for agent conversation messages
    const messagesCol = collection(db, 'agentConversations', convId, 'messages');
    const q = query(messagesCol, orderBy('timestamp', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data();
        return {
          id: docSnapshot.id,
          ...data,
          time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      });
      
      
      // ✅ FILTER: Only show messages from current agent conversation
      const filteredMsgs = msgs.filter(msg => {
        // Ensure message belongs to current conversation
        return msg.conversationId === convId || !msg.conversationId;
      });
      
      
      // Update messages in state
      dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: filteredMsgs });
      
      // Cache messages for offline access
      try {
        chatCache.cacheMessages(convId, filteredMsgs);
        offlineStorage.cacheMessages(convId, filteredMsgs);
      } catch (error) {
      }
    }, (error) => {
    });
    
    return () => {
      unsubscribe();
    };
  }, [user, conversation, firestoreConvId, localFirestoreConvId]);

  useEffect(() => {
    if (!showEmojiPicker) return;
    function handleClickOutside(event) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        dispatch({ type: 'TOGGLE_EMOJI_PICKER' });
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // 🎨 WHATSAPP-STYLE SMOOTH SCROLLING: Smart auto-scroll that preserves user position
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const lastMessageCountRef = useRef(0);
  const userScrolledUpRef = useRef(false); // ✅ FIX: Track if user manually scrolled up

  // Check if user is near bottom (within 100px)
  // const checkIfNearBottom = useCallback(() => { ... } // Moved to useScrollManagement.js

  // Smooth scroll to bottom with proper behavior
  // const scrollToBottom = useCallback((behavior = 'smooth') => { ... } // Moved to useScrollManagement.js

  // Handle scroll events to track user position
  // const handleScrollPosition = useCallback(() => { ... } // Moved to useScrollManagement.js



  // Smart auto-scroll logic
  useEffect(scrollManagement.getAutoScrollEffect(chatContainerRef, {
    allVisibleMessages,
    shouldAutoScroll,
    isNearBottom,
    setShouldAutoScroll,
    setIsNearBottom
  }), [allVisibleMessages.length, shouldAutoScroll, isNearBottom, scrollManagement]);

  // Reset auto-scroll when conversation changes
  useEffect(() => {
    setShouldAutoScroll(true);
    setIsNearBottom(true);
    userScrolledUpRef.current = false; // ✅ FIX: Reset user scroll tracking
    lastMessageCountRef.current = 0;
  }, [conversation?.id]);

  // ⚡️ MEMORY MANAGEMENT: Automatic cleanup of old pending messages
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const fiveMinutesAgo = now - (5 * 60 * 1000); // 5 minutes
      
      const cleanedPendingMessages = pendingMessages.filter(msg => {
        const msgTimestamp = msg.timestamp || msg.createdAt || msg.clientCreatedAt;
        const msgTime = typeof msgTimestamp === 'number' ? msgTimestamp : 
                       (msgTimestamp?.toDate ? msgTimestamp.toDate().getTime() : 0);
        
        // Keep messages that are:
        // 1. Less than 5 minutes old, OR
        // 2. Still in 'sending' status (might be actively being sent)
        return msgTime > fiveMinutesAgo || msg.status === 'sending';
      });
      
      // Only dispatch if we actually removed some messages
      if (cleanedPendingMessages.length < pendingMessages.length) {
        dispatch({ 
          type: 'CLEANUP_PENDING_MESSAGES', 
          payload: cleanedPendingMessages 
        });
        // Cleaned up old pending messages
      }
    }, 60 * 1000); // Run every minute
    
    return () => clearInterval(cleanupInterval);
  }, [pendingMessages.length, dispatch]);

  // 🎨 REPLY FUNCTIONALITY: Handle reply to message
  const handleReplyToMessage = useCallback((message) => {
    setReplyToMessage(message);
    // Focus the input after setting reply
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  }, []);

  // 🎨 REPLY FUNCTIONALITY: Cancel reply
  const handleCancelReply = useCallback(() => {
    setReplyToMessage(null);
  }, []);

  // 🎨 REPLY FUNCTIONALITY: Clear reply when conversation changes
  useEffect(() => {
    setReplyToMessage(null);
  }, [conversation?.id]);

  // 🎨 CONTEXT MENU: Use extracted context menu hook
  const { contextMenu, handleContextMenuAction, showContextMenu, closeContextMenu } = useContextMenu(handleReplyToMessage);


  // Listener moved to useAgentConversationListener

  // Focus the input after sending is done
  useEffect(() => {
    if (!sending && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending]);

  // Update agent chat preview in sidebar for both sent and received messages
  useEffect(() => {
    if (
      conversation &&
      conversation.id === 'admin' &&
      firestoreMessages &&
      firestoreMessages.length > 0 &&
      typeof updateAgentChatPreview === 'function'
    ) {
      const lastMsg = firestoreMessages[firestoreMessages.length - 1];
      if (lastMsg && lastMsg.text) {
        // Helper function to safely get text content
        const getTextContent = (text) => {
          if (typeof text === 'string') return text;
          if (text && typeof text === 'object' && text.sanitized) return text.sanitized;
          return '';
        };
        updateAgentChatPreview(getTextContent(lastMsg.text));
      }
    }
  }, [firestoreMessages, conversation, updateAgentChatPreview]);

  // Firestore: Real-time listener for user-to-user chat rooms (buyer-seller)
  useEffect(() => {
    if (!conversation || !user) return;
    if (conversation.buyer && conversation.seller) {
      dispatch({ type: 'SET_FIRESTORE_LOADING', payload: true });
      const messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
      // ⚡️ PERFORMANCE OPTIMIZATION: Load messages in ascending order for real-time updates
      const q = query(messagesCol, orderBy('timestamp', 'asc'));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(docSnapshot => {
          const data = docSnapshot.data();
          // If this is an order message, check if the status has been updated
          if (data.type === 'order' && data.order_id) {
            // Get the latest status from the conversation document
            const convRef = doc(db, 'sellerbuyerconversations', conversation.id);
            getDoc(convRef).then(convDoc => {
              if (convDoc.exists()) {
                const convData = convDoc.data();
                if (convData.order_id === data.order_id && convData.status !== data.status) {
                  // Update the message with the new status
                  const messageRef = doc(db, 'sellerbuyerconversations', conversation.id, 'messages', docSnapshot.id);
                  updateDoc(messageRef, {
                    status: convData.status
                  });
                }
              }
            });
          }
          return {
            id: docSnapshot.id,
            ...data,
            time: data.timestamp ? new Date(data.timestamp.toDate()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
        });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Messages are already in correct order (ascending)
        // Remove pending messages that have been confirmed by Firestore
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGES', 
          payload: pendingMessages.filter(pending => {
            const isConfirmed = msgs.some(firestore => 
              firestore.tempId && firestore.tempId === pending.id
            );
            return !isConfirmed;
          })
        });
        
        dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: msgs });
        dispatch({ type: 'SET_FIRESTORE_LOADING', payload: false });
        
        // ⚡️ PERFORMANCE OPTIMIZATION: Cache messages in IndexedDB for offline-first experience
        try {
          offlineStorage.cacheMessages(conversation.id, msgs);
        } catch (error) {
        }
      });
      return () => unsubscribe();
    }
  }, [conversation, user]);

  // Always use Firestore messages for buyer-seller conversations
  const isUserAgentChat = conversation && (conversation.id === 'admin' && !isAdminDashboard || !!(firestoreConvId || localFirestoreConvId));
  const isUserToUserChat = conversation && conversation.buyer && conversation.seller;
  
  // ✅ FIX: Filter messages to only show current conversation messages
  const chatMessages = useMemo(() => {
    if (isUserAgentChat) {
      // For agent conversations, ensure messages belong to current conversation
      const convId = firestoreConvId || localFirestoreConvId;
      if (convId) {
        return firestoreMessages.filter(msg => 
          msg.conversationId === convId || !msg.conversationId
        );
      }
      return firestoreMessages;
    } else if (isUserToUserChat) {
      // For user-to-user conversations, ensure messages belong to current conversation
      return firestoreMessages.filter(msg => 
        msg.conversationId === conversation.id || !msg.conversationId
      );
    }
    return [];
  }, [isUserAgentChat, isUserToUserChat, firestoreMessages, firestoreConvId, localFirestoreConvId, conversation?.id]);

  // 🚀 ASC ORDER GUARANTEE: chatMessages are sorted oldest→newest (ASC) as required by useLazyMessages
  // This ensures zero-copy windowing works correctly and virtualization remains stable
  // Now safe to use chatMessages below
  // Helper to check if the uploading file's URL is in chatMessages
  const fileMessageInChat = uploadedFileUrl && chatMessages.some(msg => msg && msg.url === uploadedFileUrl);

  // Only hide preview when the message is visible in chat
  useEffect(() => {
    if (
      uploading &&
      uploadingFile &&
      uploadedFileUrl &&
      chatMessages.some(msg => msg && msg.url === uploadedFileUrl)
    ) {
        dispatch({ type: 'SET_UPLOADING', payload: false });
        dispatch({ type: 'SET_UPLOADING_FILE', payload: null });
        dispatch({ type: 'SET_UPLOADED_FILE_URL', payload: null });
    }
  }, [chatMessages, uploading, uploadingFile, uploadedFileUrl]);



  // Send file message
  // const handleSendFileMessage = async (url, type) => { ... } // Moved to useFileHandling.js

  // Determine the other party's name, avatar, and online status for the header
  let otherPartyName = '';
  let otherPartyAvatar = '';
  const [resolvedHeaderAvatar, setResolvedHeaderAvatar] = useState(null);
  
  
  const avatarBg = isDarkMode ? '2a2a2a' : 'dddddd';
  const avatarColor = isDarkMode ? 'ffffff' : '222222';
  const buildAvatar = useCallback((name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${avatarBg}&color=${avatarColor}&rounded=true&size=64`, [avatarBg, avatarColor]);
  // Header-only avatar: force background to 0f0f0f in dark mode
  const headerBg = isDarkMode ? '0f0f0f' : 'dddddd';
  const buildHeaderAvatar = useCallback((name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${headerBg}&color=${avatarColor}&rounded=true&size=64`, [headerBg, avatarColor]);
  
  if (isAdminDashboard && conversation && (conversation.userEmail || conversation.userName)) {
    otherPartyName = conversation.userName || conversation.userEmail || 'Unknown User';
    otherPartyAvatar = resolvedHeaderAvatar || conversation.avatar || buildHeaderAvatar(otherPartyName);
  } else if (user && conversation && conversation.buyer && conversation.seller) {
    // Check if current user is the buyer
    
    if (conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase()) {
      // Current user is buyer, show seller info
      otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || 'Seller';
      otherPartyAvatar = resolvedHeaderAvatar || conversation.seller.avatar || conversation.seller_avatar || buildHeaderAvatar(otherPartyName);
    } else if (conversation.seller.email && conversation.seller.email.toLowerCase() === user.email.toLowerCase()) {
      // Current user is seller, show buyer info
      otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || 'Buyer';
      otherPartyAvatar = resolvedHeaderAvatar || conversation.buyer.avatar || conversation.buyer_avatar || buildHeaderAvatar(otherPartyName);
    } else {
      // Fallback: try to determine based on UID
      if (conversation.buyer.uid && conversation.buyer.uid === user.uid) {
        otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || 'Seller';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.seller.avatar || conversation.seller_avatar || buildHeaderAvatar(otherPartyName);
      } else if (conversation.seller.uid && conversation.seller.uid === user.uid) {
        otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || 'Buyer';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.buyer.avatar || conversation.buyer_avatar || buildHeaderAvatar(otherPartyName);
      } else {
        // Additional fallback: if we can't determine, show the other party's info
        // This handles cases where email/UID matching fails
        const isCurrentUserBuyer = conversation.buyer.email === user.email || conversation.buyer.uid === user.uid;
        if (isCurrentUserBuyer) {
          otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || 'Seller';
          otherPartyAvatar = resolvedHeaderAvatar || conversation.seller.avatar || conversation.seller_avatar || buildHeaderAvatar(otherPartyName);
        } else {
        otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || 'Buyer';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.buyer.avatar || conversation.buyer_avatar || buildHeaderAvatar(otherPartyName);
        }
      }
    }
  }
  
  // Final fallback: if we still don't have a name, try to determine from conversation data
  if (!otherPartyName && conversation) {
    
    if (conversation.buyer && conversation.seller) {
      // If we have both buyer and seller, show the one that's not the current user
      const currentUserEmail = user?.email?.toLowerCase();
      const currentUserUid = user?.uid;
      
      const buyerEmail = conversation.buyer.email?.toLowerCase();
      const sellerEmail = conversation.seller.email?.toLowerCase();
      
      
      if (buyerEmail === currentUserEmail || conversation.buyer.uid === currentUserUid) {
        // Current user is buyer, show seller
        otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || conversation.seller.seller_username || 'Seller';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.seller.avatar || conversation.seller_avatar || buildHeaderAvatar(otherPartyName);
        // Showing seller
      } else if (sellerEmail === currentUserEmail || conversation.seller.uid === currentUserUid) {
        // Current user is seller, show buyer
        otherPartyName = conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || conversation.buyer.buyer_username || 'Buyer';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.buyer.avatar || conversation.buyer_avatar || buildHeaderAvatar(otherPartyName);
        // Showing buyer
      } else {
        // Fallback: show the first available name
        otherPartyName = conversation.seller.name || conversation.seller_name || conversation.seller.username || conversation.seller.seller_username || 
                        conversation.buyer.name || conversation.buyer_name || conversation.buyer.username || conversation.buyer.buyer_username || 'User';
        otherPartyAvatar = resolvedHeaderAvatar || conversation.seller.avatar || conversation.seller_avatar || 
                          conversation.buyer.avatar || conversation.buyer_avatar || 
                           buildHeaderAvatar(otherPartyName);
        // Fallback name
      }
    } else {
      // For conversations without buyer/seller structure
      otherPartyName = conversation.name || conversation.userName || conversation.userEmail || 'User';
      otherPartyAvatar = resolvedHeaderAvatar || conversation.avatar || buildHeaderAvatar(otherPartyName);
      // No buyer/seller structure
    }
  }

  // ✅ FIX: Special handling for agent conversations - use YTproud logo
  if (conversation && conversation.id === 'admin') {
    otherPartyName = 'YTproud Agent';
    otherPartyAvatar = '/images/ytproud.png'; // Use YTproud logo for agent conversations
  }

  // Add debounce to handleSend and handleSendAgentMessage:
  const lastSentRef = useRef(0);
  const MIN_SEND_INTERVAL = 1000; // 1 second

  // ⚡️ WHATSAPP-STYLE BACKGROUND PROCESSING: Handle server communication with proper sync
  // ⚡️ WHATSAPP-STYLE BACKGROUND PROCESSING: Now imported from ./chat/hooks/useMessageProcessing.js

  const handleSendAgentMessage = async (e, instantMessage = null) => {
    e.preventDefault();
    if (!user) return;
    
    // ⚡️ WHATSAPP-STYLE INSTANT DISPLAY: If instant message provided, display immediately
    if (instantMessage && instantMessage.isOptimistic) {
      // ⚡️ INSTANT UI UPDATE: Add message to chat immediately (0ms delay)
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: instantMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
      
      // ⚡️ BACKGROUND PROCESSING: Start server communication in background
      setTimeout(() => {
        messageProcessing.processMessageInBackground(instantMessage, {
          sanitizeText,
          conversation,
          user,
          isAdminDashboard,
          firestoreConvId,
          localFirestoreConvId,
          isConnected,
          sendMessage,
          serverTimestamp,
          updateAgentChatPreview,
          moveConversationToTop
        });
      }, 0);
      
      return; // Exit early to prevent double processing
    }
    
    // 🛡️ REMOVED CLIENT-SIDE RATE LIMITING - Now handled server-side via API
    
    // Security: Input validation
    const inputValidation = validateInput(input);
    if (!inputValidation.isValid) {
      if (typeof toast !== 'undefined') {
        toast.error(inputValidation.error);
      } else {
        alert(inputValidation.error);
      }
      return;
    }
    
    const messageText = input.trim();
    
    // ⚡️ INSTANT UI UPDATE: Clear input immediately (before any processing)
    dispatch({ type: 'SET_INPUT', payload: '' });
    
    // ⚡️ STEP 1: Create temporary message with unique ID for instant display
    const tempId = instantMessage ? instantMessage.id : `temp_${Date.now()}_${Math.random()}`;
    const tempMessage = instantMessage || {
      id: tempId,
      text: messageText, // Use original text for instant display
      from: user.email,
      fromUid: user.uid || user.id || user.email,
      time: new Date().toLocaleTimeString(),
      status: 'sending',
      timestamp: new Date(),
      isPending: true
    };

    // ⚡️ STEP 1: Show message instantly in chat panel (like WhatsApp)
    if (!instantMessage) {
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: tempMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
    }
    
    // Security: Enhanced sanitization (after instant display) using Web Worker
    const sanitizedText = (await sanitizeText(messageText)).sanitized;
    
    // Security: Log message sending
    logSecurityEvent('MESSAGE_SENT', { 
      conversationId: conversation?.id, 
      messageLength: sanitizedText.length,
      isAgent: true 
    });
    let convId = firestoreConvId || localFirestoreConvId;
    if (!convId && !isAdminDashboard) {
      convId = `agent_${user.uid || user.id || user.email}`;
    }
    if (!convId) {
      dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    
    // 🛡️ REMOVED CLIENT-SIDE CSRF - Now handled server-side for security
    if (user && isConnected && conversation && conversation.id) {
      sendMessage(sanitizedText, convId);
    }
    
    // Security: Save sanitized message to Firestore
    const messagesCol = collection(db, 'agentConversations', convId, 'messages');
    
    // Security: Validate user data before saving
    if (!user || !user.email) {
      if (typeof toast !== 'undefined') {
        toast.error('Authentication error. Please refresh and try again.');
      }
        dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    
    // ⚡️ STEP 2: Upload real message to Firestore
    try {
      // ✅ FIX: Ensure all required fields have valid values
      const messageData = {
        from: user.email,
        fromUid: user.uid || user.id || user.email, // Fallback to email if uid is undefined
        type: 'text',
        text: sanitizedText, // ✅ SANITIZED
        timestamp: serverTimestamp(),
        readBy: [user.email],
        readByAdmin: false, // ✅ FIX: Always set to false for user messages
        conversationId: convId,
        isAgent: false, // ✅ FIX: User messages are not agent messages
        status: 'sent'
      };
      
      // Validate message data before saving
      if (!messageData.from || !messageData.fromUid || !messageData.text) {
        throw new Error('Invalid message data: missing required fields');
      }
      
      const docRef = await addDoc(messagesCol, messageData);
    
    // Update the conversation document
    await updateDoc(doc(db, 'agentConversations', convId), {
      lastMessage: messageText,
      lastMessageTime: serverTimestamp(),
    });
    
    // ⚡️ STEP 2: Replace temp message with real one (like WhatsApp)
    const realMessage = {
      id: docRef.id,
      text: sanitizedText,
      from: user.email,
      fromUid: user.uid || user.id || user.email,
      time: new Date().toLocaleTimeString(),
      status: 'sent',
      timestamp: new Date(),
      firestoreId: docRef.id
    };
    
    // Replace the temp message with the real one
    dispatch({ 
      type: 'REPLACE_PENDING_MESSAGE', 
      payload: { tempId, realMessage }
    });
    
    dispatch({ type: 'SET_SENDING', payload: false });
    dispatch({ type: 'SET_SHOW_TICK', payload: true });
    setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
    
      } catch (error) {
      // ⚡️ STEP 3: Handle upload failure (like WhatsApp)
      
      // Update temp message to show failed status
      dispatch({ 
        type: 'REPLACE_PENDING_MESSAGE', 
        payload: { 
          tempId, 
          realMessage: {
            id: tempId,
            text: sanitizedText,
            from: user.email,
            fromUid: user.uid || user.id || user.email,
            time: new Date().toLocaleTimeString(),
            status: 'failed',
            timestamp: new Date(),
            error: error.message
          }
        }
      });
      
      dispatch({ type: 'SET_SENDING', payload: false });
      
      // Show error to user
      if (typeof toast !== 'undefined') {
        toast.error('Failed to send message. Please try again.');
      } else {
        alert('Failed to send message. Please try again.');
      }
    }
  };

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize send message handler
  const handleSendMessage = useCallback(async (messageText, instantMessage = null) => {
    if (!conversation || !user) return;
    
    // ⚡️ INSTANT DISPLAY: If instant message provided, display immediately
    if (instantMessage) {
      dispatch({ type: 'ADD_PENDING_MESSAGE', payload: instantMessage });
      dispatch({ type: 'SET_SENDING', payload: true });
    }
    
    // Security: Enhanced sanitization using Web Worker
    const sanitizedText = (await sanitizeText(messageText)).sanitized;
    
    // Security: Log message sending
    logSecurityEvent('MESSAGE_SENT', { 
      conversationId: conversation.id, 
      messageLength: sanitizedText.length,
      isAgent: false 
    });
    
      // Enhanced offline handling with local caching
  if (!isOnline) {
    const offlineMessage = {
      from: user.email,
      fromUid: user.uid || user.id || user.email, // Security: Store UID for server-side validation
      text: sanitizedText,
      timestamp: new Date(),
      type: 'offline',
      conversationId: conversation.id,
      // Add integrity hash for offline messages too
      integrityHash: CryptoJS.SHA256(
        `${sanitizedText}${user.email}${Date.now()}`
      ).toString()
    };
    
    // Add to offline queue for sync when back online
    addToOfflineQueue(offlineMessage);
    
    // Add message to UI immediately with offline indicator
    const tempMessage = {
      id: `offline_${Date.now()}`,
      from: user.email,
      text: sanitizedText, // ✅ SANITIZED
      time: new Date().toLocaleTimeString(),
      status: 'offline',
      isOffline: true
    };
    dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, tempMessage] });
    
    // Security: Log offline message creation
    logSecurityEvent('OFFLINE_MESSAGE_CREATED', {
      conversationId: conversation.id,
      messageLength: sanitizedText.length
    }, 'info');
    
    return;
  }

    // 🚀 MESSAGE IDEMPOTENCY: Generate unique client message ID for deduplication
    const clientMessageId = crypto.randomUUID();
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage = {
      id: tempId,
      from: user.email,
      fromUid: user.uid || user.id || user.email, // Security: Store UID for server-side validation
      text: sanitizedText,
      time: new Date().toLocaleTimeString(),
      status: 'sending', // ⏳ Show sending status
      timestamp: new Date(),           // keep for UI
      clientCreatedAt: Date.now(),     // ← use in sort fallback
      isPending: true,
      isOptimistic: true,              // ← important
      clientMessageId,                 // 🚀 IDEMPOTENCY: Unique ID for server deduplication
      replyTo: replyToMessage ? {      // 🎨 REPLY: Include reply information (sanitized)
        id: replyToMessage.id || null,
        text: replyToMessage.text || null,
        from: replyToMessage.from || null,
        name: replyToMessage.name || null,
        type: replyToMessage.type || 'text'
      } : null
    };

    // Add to pending messages immediately (no Firestore reload)
    dispatch({ type: 'ADD_PENDING_MESSAGE', payload: optimisticMessage });
    
    // 🎨 REPLY: Clear reply immediately (no delay)
    setReplyToMessage(null);
    
    dispatch({ type: 'SET_SENDING', payload: true });
    if (conversation.id === 'admin' || (isAdminDashboard && activeTab === 'agent')) {
      
      // ✅ FIX: Pass the optimistic message to handleSendAgentMessage
      try {
        await handleSendAgentMessage({ preventDefault: () => {} }, optimisticMessage);
      } catch (error) {
      }
      
      dispatch({ type: 'SET_SENDING', payload: false });
      dispatch({ type: 'SET_SHOW_TICK', payload: true });
      setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
      return;
    }
    const senderId = user?.uid || user?.email || 'unknown';
    if (conversation.buyer && conversation.seller) {
      try {
      // 🛡️ SECURITY: Send message via secure API endpoint with server-side validation
      try {
        const response = await fetch('/api/chat/send-message', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await user?.getIdToken()}`
          },
          body: JSON.stringify({
            text: sanitizedText,
            conversationId: conversation.id,
            type: 'text',
            replyTo: replyToMessage ? {
              id: replyToMessage.id || null,
              text: replyToMessage.text || null,
              from: replyToMessage.from || null,
              name: replyToMessage.name || null,
              type: replyToMessage.type || 'text'
            } : null
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to send message');
        }
        
        const result = await response.json();
        
        // Update the conversation document
        await updateDoc(doc(db, 'sellerbuyerconversations', conversation.id), {
          lastMessage: sanitizedText,
          lastMessageTime: serverTimestamp(),
        });
        
        // Update pending message with success
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { id: tempId, status: 'sent', messageId: result.messageId }
        });
        
      } catch (error) {
        
        // Update pending message with error
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGE', 
          payload: { id: tempId, status: 'error', error: error.message }
        });
        
        // Fallback to Firestore if API fails
        const messagesCol = collection(db, 'sellerbuyerconversations', conversation.id, 'messages');
      const docRef = await addDoc(messagesCol, {
        from: user.email,
          fromUid: user.uid || user.email,
        type: 'text',
          text: sanitizedText,
        timestamp: serverTimestamp(),
          clientCreatedAt: optimisticMessage.clientCreatedAt,
          tempId,
        readBy: [user.email],
          replyTo: replyToMessage ? {
          id: replyToMessage.id || null,
          text: replyToMessage.text || null,
          from: replyToMessage.from || null,
          name: replyToMessage.name || null,
          type: replyToMessage.type || 'text'
        } : null
      });
      
      // Update the conversation document
      await updateDoc(doc(db, 'sellerbuyerconversations', conversation.id), {
        lastMessage: sanitizedText,
        lastMessageTime: serverTimestamp(),
      });
      }
      
      // Send notifications to the RECIPIENT (not the sender)
      try {
        // Determine who the recipient is (the other person in the conversation)
        const currentUserEmail = user.email.toLowerCase();
        const buyerEmail = conversation.buyer.email?.toLowerCase();
        const sellerEmail = conversation.seller.email?.toLowerCase();
        
        let recipientEmail = null;
        
        if (currentUserEmail === buyerEmail) {
          // Current user is buyer, so recipient is seller
          recipientEmail = sellerEmail;
        } else if (currentUserEmail === sellerEmail) {
          // Current user is seller, so recipient is buyer
          recipientEmail = buyerEmail;
        }
        
        if (recipientEmail && recipientEmail !== currentUserEmail) {
          // 1. Send Telegram notification
          const telegramResponse = await fetch('/api/telegram/notify-new-message', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              senderEmail: user.email,
              recipientEmail: recipientEmail,
              message: sanitizedText,
              conversationId: conversation.id
            })
          });
          
          const telegramResult = await telegramResponse.json();
          if (!telegramResult.success) {
          }

          // 2. Send Email notification
          const emailResponse = await fetch('/api/notifications/send-message-email', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              senderEmail: user.email,
              senderName: user.username || user.displayName || user.email.split('@')[0],
              recipientEmail: recipientEmail,
              messagePreview: sanitizedText.length > 100 ? sanitizedText.substring(0, 100) + '...' : sanitizedText,
              conversationId: conversation.id
            })
          });
          
          const emailResult = await emailResponse.json();
          if (!emailResult.success) {
          } else {
          }
        } else {
          // No notification sent - recipient not found or same as sender
        }
      } catch (notificationError) {
        // Don't fail the message send if notification fails
      }
      
      // Update optimistic message status to 'sent' (no reload needed)
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGES', 
        payload: pendingMessages.map(msg => 
          msg.id === tempId && msg.status === 'sending' 
            ? { ...msg, status: 'sent' }
            : msg
        )
      });
      
      dispatch({ type: 'SET_SENDING', payload: false });
      dispatch({ type: 'SET_SHOW_TICK', payload: true });
      setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
      return;
      } catch (error) {
        
        // Update optimistic message to show failed status
        dispatch({ 
          type: 'UPDATE_PENDING_MESSAGES', 
          payload: pendingMessages.map(msg => 
            msg.id === tempId && msg.status === 'sending' 
              ? { ...msg, status: 'failed', error: error.message }
              : msg
          )
        });
        
        dispatch({ type: 'SET_SENDING', payload: false });
        
        if (typeof toast !== 'undefined') {
          toast.error('Failed to send message. Please try again.');
        } else {
          alert('Failed to send message. Please try again.');
        }
        return;
      }
    }
    if (activeTab !== 'transactions') {
      dispatch({ type: 'SET_SENDING', payload: false });
      return;
    }
    const newMsg = {
      id: currentMessages.length + 1,
      from: senderId,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      text: sanitizedText,
    };
    dispatch({ type: 'SET_FIRESTORE_MESSAGES', payload: [...firestoreMessages, newMsg] });
    if (moveConversationToTop) {
      moveConversationToTop(conversation.id, sanitizedText);
    }
    if (updateMessagesPreview) updateMessagesPreview(conversation.id, sanitizedText);
    dispatch({ type: 'SET_SENDING', payload: false });
    dispatch({ type: 'SET_SHOW_TICK', payload: true });
    setTimeout(() => dispatch({ type: 'SET_SHOW_TICK', payload: false }), 1000);
  }, [conversation, user, isOnline, addToOfflineQueue, dispatch, isAdminDashboard, activeTab, handleSendAgentMessage, isConnected, sendMessage, currentMessages, moveConversationToTop, updateMessagesPreview, replyToMessage]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced file upload with background processing
  // const handleFileUpload = useCallback(async (file) => { ... } // Moved to useFileHandling.js

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize image click handler
  // const handleImageClick = useCallback((imageUrl) => { ... } // Moved to useFileHandling.js

  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced debounced typing indicator with throttling
  // Typing indicator extracted to hook
  const { isTyping, setTyping: debouncedTypingIndicator } = useTypingIndicator({
    isConnected,
    sendMessage,
    conversationId: conversation?.id
  });

  const handleEmojiClick = (emoji) => {
    // Security: Validate emoji before adding
    if (!validateEmoji(emoji)) {
      logSecurityEvent('INVALID_EMOJI_ATTEMPT', { emoji }, 'warning');
      if (typeof toast !== 'undefined') {
        toast.error('Invalid emoji detected');
      }
      return;
    }
    
    dispatch({ type: 'SET_INPUT', payload: input + emoji });
    dispatch({ type: 'TOGGLE_EMOJI_PICKER' });
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Trigger typing indicator
    debouncedTypingIndicator(true);
  };

  // Drag & Drop handlers
  // const handleDragOver = (e) => { ... } // Moved to useFileHandling.js
  // const handleDragLeave = (e) => { ... } // Moved to useFileHandling.js
  // const handleDrop = async (e) => { ... } // Moved to useFileHandling.js



  const canSendMessage = true;

  // ⚡️ PERFORMANCE OPTIMIZATION: Socket message deduplication - Now imported from ./chat/hooks/useMessageProcessing.js

  // ⚡️ SECURITY: Socket message validation - Now imported from ./chat/hooks/useMessageProcessing.js

  // ⚡️ UTILITY: Get deduplication cache statistics - Now imported from ./chat/hooks/useMessageProcessing.js

  // 🚀 MICRO-BATCHING: Batch incoming messages to prevent render thrashing
  const batchRef = useRef([]);
  const flushTimer = useRef(null);

  // 🚀 ENHANCED IDLE-TIME PRECOMPUTATION: Systematic warming for optimal performance
  useEffect(() => {
    const id = requestIdleCallback(async () => {
      try {
        // 🚀 SYSTEMATIC WARMING: Precompute next 200 messages above viewport
        const nextSlice = firestoreMessages.slice(-400, -200);
        
        if (nextSlice.length > 0) {
          // 🚀 WORKER WARMING: Pre-process messages through worker
          await processMessagesWithWorker(nextSlice, { 
            sanitize: true, 
            hash: true, 
            precompute: true 
          });
          
          // 🚀 HEIGHT CACHE WARMING: Pre-calculate heights for smooth scrolling
          nextSlice.forEach((msg, idx) => {
            // This will be used by VirtualizedMessageList's height cache
            const cacheKey = msg.id || `${msg.timestamp || Date.now()}-${idx}`;
            // Height calculation logic will be handled by VirtualizedMessageList
          });
          
          // 🚀 AVATAR PRELOADING: Preload next sender avatars
          const uniqueSenders = [...new Set(nextSlice.map(msg => msg.from))];
          uniqueSenders.slice(0, 5).forEach(sender => {
            // Preload avatar if available
            if (sender && sender !== user?.email) {
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              link.href = `https://api.dicebear.com/7.x/avataaars/svg?seed=${sender}`;
              document.head.appendChild(link);
            }
          });
          
          // Enhanced idle-time precomputed messages and avatars
        }
      } catch (error) {
      }
    }, { timeout: 300 });
    
    return () => cancelIdleCallback(id);
  }, [firestoreMessages, processMessagesWithWorker, user?.email]);

  // ⚡️ PERFORMANCE MONITORING: Use extracted performance monitoring hook
  useEffect(() => {
    return performanceMonitoring.trackRenderPerformance(allVisibleMessages.length);
  });

  // ⚡️ ERROR BOUNDARY: Enhanced error handling for message processing
  const handleMessageProcessingError = useCallback((error, context) => {
    
    // Log to analytics/monitoring service
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `ChatPanel: ${context} - ${error.message}`,
        fatal: false
      });
    }
    
    // Show user-friendly error message
    toast.error(`Message processing error. Please try again.`);
    
    // Attempt recovery
    if (context === 'message_validation') {
      // Skip problematic message
      return false;
    }
    
    return true;
  }, []);

  // ⚡️ MEMORY LEAK PREVENTION: Use extracted performance monitoring hook
  const performMemoryCleanup = useCallback(() => {
    return performanceMonitoring.performMemoryCleanup();
  }, [performanceMonitoring]);

  // ⚡️ MEMORY LEAK PREVENTION: Periodic comprehensive cleanup
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      performMemoryCleanup();
    }, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(cleanupInterval);
  }, [performMemoryCleanup]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Extract message processing logic - Now imported from ./chat/hooks/useMessageProcessing.js

  // 🚀 REACT 18 CONCURRENCY: Defer heavy message processing to prevent UI blocking
  const deferredMessages = useDeferredValue(chatMessages);
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Enhanced message filtering with background processing
  const sortedMergedMessages = useMemo(() => {
    // Use deferred messages for better performance
    const messagesToProcess = deferredMessages;
    
    // ⚡️ PERFORMANCE OPTIMIZATION: Use requestIdleCallback for heavy processing only for very large message lists
    if (window.requestIdleCallback && messagesToProcess.length > 500) {
      // For very large lists, process in background but return immediately with cached result
      requestIdleCallback(() => {
        messageProcessing.processMessages(messagesToProcess, isAdminDashboard, conversation, user?.email);
      });
      // Return cached or processed result immediately
      return messageProcessing.processMessages(messagesToProcess, isAdminDashboard, conversation, user?.email);
    }
    
    return messageProcessing.processMessages(messagesToProcess, isAdminDashboard, conversation, user?.email);
  }, [deferredMessages, isAdminDashboard, conversation, user?.email, messageProcessing.processMessages]);


  // 🎨 ANIMATION: Message bubble animation variants
  const messageVariants = {
    hidden: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95 
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };





  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize message status renderer
  const renderMessageStatus = useCallback((msg) => {
      return (
      <MessageStatus
        msg={msg}
        user={user}
        conversation={conversation}
        isDarkMode={isDarkMode}
      />
    );
  }, [user, conversation, isDarkMode]);



  // Join the room when a conversation is selected


  // 🚀 MICRO-BATCHING: Enqueue incoming messages for batched processing
  // const enqueueIncoming = useCallback((validatedMsg) => { ... } // Moved to useSocketChat.js

  // Security: Listen for new messages via socket with validation and deduplication
  useEffect(() => {
    const handleSocketMessage = async (msg) => {
      await socketChat.handleSocketMessage(msg, {
        messageProcessing,
        sanitizeText,
        conversation,
        pendingMessages,
        firestoreMessages,
        dispatch,
        enqueueIncoming: (validatedMsg) => socketChat.enqueueIncoming(validatedMsg, {
          firestoreMessages,
          processMessagesWithWorker,
          dispatch
        })
      });
    };
    const unsubscribe = onMessage(handleSocketMessage);
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [onMessage, conversation, deduplicateMessage, validateSocketMessage, socketChat]);

  // Handle optimistic UI updates when Firestore messages come back
  useEffect(() => {
    // When new messages come from Firestore, remove matched pending messages
    const updateOptimisticMessages = () => {
      dispatch({ 
        type: 'UPDATE_PENDING_MESSAGES', 
        payload: pendingMessages.filter(pending => {
          // Remove pending messages that have been confirmed by Firestore
          const isConfirmed = firestoreMessages.some(firestore => 
            firestore.tempId && firestore.tempId === pending.id
          );
          return !isConfirmed;
        })
      });
    };

    updateOptimisticMessages();
  }, [firestoreMessages]);

  // Resolve header avatar from backend if missing
  const isRealAvatar = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    if (u.includes('ui-avatars.com') || u.includes('pravatar.cc')) return false;
    return u.startsWith('http') || u.startsWith('data:image/');
  }, []);

  useEffect(() => {
    if (!conversation || conversation.id === 'admin') return;
    if (isRealAvatar(resolvedHeaderAvatar)) return;
    const currentAv = otherPartyAvatar;
    if (isRealAvatar(currentAv)) return;
    const usernameCandidate = otherPartyName || conversation.userName || conversation.name || '';
    const uname = String(usernameCandidate || '').trim();
    if (!uname) return;
    let aborted = false;
    (async () => {
      try {
        const res = await apiGet(`/api/users/public/${encodeURIComponent(uname)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (aborted) return;
        if (data && isRealAvatar(data.avatar)) {
          setResolvedHeaderAvatar(data.avatar);
        }
      } catch (_) {}
    })();
    return () => { aborted = true; };
  }, [conversation?.id, otherPartyName, otherPartyAvatar, isRealAvatar]);

  return (
          <section className={`flex-1 flex flex-col h-full ${
            isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f]' : 'bg-gradient-to-br from-gray-50 to-gray-100'
          }`}>
      {/* Header - Only show when conversation is selected */}
      {conversation && (
        <ChatHeader
          conversation={conversation}
          otherPartyName={otherPartyName}
          otherPartyAvatar={otherPartyAvatar}
          otherPartyOnline={otherPartyOnline}
          otherPartyLastSeen={otherPartyLastSeen}
          isDarkMode={isDarkMode}
          onSelectConversation={onSelectConversation}
          dispatch={dispatch}
          buildHeaderAvatar={buildHeaderAvatar}
        />
      )}
      {/* Chat Messages (including order details card at the top) */}
      <div 
        className={`flex-1 min-h-0 px-3 sm:px-6 py-2 overflow-y-auto flex flex-col gap-3 sm:gap-4 custom-scrollbar relative ${isDragOver ? 'bg-blue-50 border-2 border-dashed border-blue-300' : ''}`}
        ref={chatContainerRef}
        onDragOver={(e) => fileHandling.handleDragOver(e, dispatch)}
        onDragLeave={(e) => fileHandling.handleDragLeave(e, dispatch)}
        onDrop={(e) => fileHandling.handleDrop(e, dispatch, (file) => fileHandling.handleFileUpload(file, {
          user,
          conversation,
          dispatch,
          chatCache,
          handleSendFileMessage: (url, type) => fileHandling.handleSendFileMessage(url, type, {
            user,
            conversation,
            isAdminDashboard,
            firestoreConvId,
            dispatch,
            pendingMessages
          })
        }))}
      >
        {/* Drag & Drop Overlay */}
        <DragDropOverlay isDragOver={isDragOver} isDarkMode={isDarkMode} />

        {/* Loading indicator for older messages */}
        {loadingMore && (
          <div className="flex justify-center items-center py-2">
            <span className="rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></span>
            <span className={`ml-2 text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-500'
            }`}>Loading older messages...</span>
          </div>
        )}
        
        {/* --- NEW: Show order/escrow/status boxes if transaction conversation and no order message --- */}
        {conversation && conversation.type === 'transaction' && !sortedMergedMessages.some(m => m.type === 'order') && (
          <div className={`mb-3 w-full rounded-xl shadow-lg border transition-all duration-300 hover:shadow-xl px-4 py-3 ${
            isDarkMode ? 'bg-gradient-to-br from-[#0f0f0f] to-[#0f0f0f] border-gray-700/50' : 'bg-white border-gray-200'
          }`}>
            <MarketplaceHeader
              conversation={conversation}
              isDarkMode={isDarkMode}
              dispatch={dispatch}
              state={state}
              isAdmin={isAdminDashboard}
            />
              <OrderDetailsCard
                conversation={conversation}
                isDarkMode={isDarkMode}
                dispatch={dispatch}
                state={state}
                isAdmin={isAdminDashboard}
              />
            <EscrowWorksBox
              conversation={conversation}
              isDarkMode={isDarkMode}
              dispatch={dispatch}
              state={state}
            />
            <TransactionStatusBox
              conversation={conversation}
              isDarkMode={isDarkMode}
              dispatch={dispatch}
              state={state}
              isAdmin={isAdminDashboard}
            />
            <LoginDetailsBox
              conversation={conversation}
              isDarkMode={isDarkMode}
              isAdminDashboard={isAdminDashboard}
              user={user}
            />
          </div>
        )}
        
        {/* Messages */}
        {!conversation ? (
          <div className="hidden sm:flex flex-col items-center justify-center h-full px-4">
            {/* Empty state - no conversation selected */}
          </div>
        ) : sortedMergedMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-10">
            <div className="text-2xl font-bold text-gray-700 mb-2">Welcome!</div>
            <div className="text-base text-gray-500 text-center max-w-md">
              {(() => {
                if (user && conversation && conversation.buyer && conversation.seller) {
                  if (conversation.buyer.email && conversation.buyer.email.toLowerCase() === user.email.toLowerCase()) {
                    return 'Your conversation has started 🎉 You can now start chatting with the seller.';
                  } else if (conversation.seller.email && conversation.seller.email.toLowerCase() === user.email.toLowerCase()) {
                    return 'Your conversation has started 🎉 You can now start chatting with the buyer.';
                  }
                }
                return 'Your conversation has started 🎉 You can now start chatting.';
              })()}
            </div>
          </div>
        ) : (
          // ⚡️ PERFORMANCE OPTIMIZATION: Show pending messages immediately + lazy loaded messages
          <>
            {/* ⚡️ INSTANT DISPLAY: All messages (pending + existing) with virtualization and animations */}
            <MessageList
              allVisibleMessages={allVisibleMessages}
                user={user}
                isAdminDashboard={isAdminDashboard}
                conversation={conversation}
                onImageClick={(imageUrl) => fileHandling.handleImageClick(imageUrl, dispatch)}
                renderMessageStatus={renderMessageStatus}
                onReplyClick={handleReplyToMessage}
                onContextMenu={showContextMenu}
              isDarkMode={isDarkMode}
              fileHandling={fileHandling}
              dispatch={dispatch}
              />
            </>
            )}

        {/* Loading indicator removed for cleaner interface */}

        {/* WhatsApp-style Upload Progress Message */}
        {uploading && uploadingFile && (
          <div className="flex justify-end mb-4">
            <div className="max-w-xs sm:max-w-sm bg-red-500 text-white rounded-2xl rounded-br-md px-4 py-3 relative overflow-hidden">
              {/* File Preview */}
              <div className="relative mb-3">
                {uploadingFile.type.startsWith('image/') ? (
                  <img 
                    src={uploadingFile.previewUrl} 
                    alt="Uploading preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : uploadingFile.type.startsWith('video/') ? (
                  <video 
                    src={uploadingFile.previewUrl} 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : null}
                
                {/* Upload Progress Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                  <div className="text-center">
                    {/* Circular Progress */}
                    <div className="relative w-16 h-16 mx-auto mb-2">
                      <svg width="64" height="64" viewBox="0 0 64 64">
                <circle
                          cx="32" 
                          cy="32" 
                          r="28" 
                          stroke="rgba(255,255,255,0.3)" 
                          strokeWidth="4" 
                          fill="none" 
                        />
                        <circle
                          cx="32"
                          cy="32"
                          r="28"
                          stroke="#10B981"
                          strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 28}
                          strokeDashoffset={2 * Math.PI * 28 * (1 - uploadProgress / 100)}
                  style={{ transition: 'stroke-dashoffset 0.2s linear' }}
                />
              </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">{Math.round(uploadProgress)}%</span>
              </div>
            </div>
                    <div className="text-white text-sm font-medium">Uploading...</div>
                  </div>
                </div>
              </div>
              
              {/* File Info */}
              <div className="text-sm font-medium truncate">{uploadingFile.name}</div>
              <div className="text-xs text-red-100 opacity-80">
                {Math.round(uploadingFile.size / 1024)} KB • {uploadingFile.type.split('/')[1].toUpperCase()}
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-red-400 rounded-full h-1 mt-2">
                <div 
                  className="bg-white h-1 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} style={{ height: 0, margin: 0, padding: 0 }} />
        
        {/* 🎨 WHATSAPP-STYLE: Scroll to bottom button */}
        {!isNearBottom && (
          <button
            onClick={() => {
              scrollManagement.scrollToBottom(chatContainerRef, 'smooth');
              setShouldAutoScroll(true);
              userScrolledUpRef.current = false; // ✅ FIX: Reset user scroll tracking when manually scrolling to bottom
            }}
            className="fixed bottom-24 right-6 z-40 w-12 h-12 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center"
            aria-label="Scroll to bottom"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
      </div>

      {/* 🎨 CONTEXT MENU: Render context menu */}
      <MessageContextMenu
        message={contextMenu.message}
        isVisible={contextMenu.isVisible}
        position={contextMenu.position}
        onClose={closeContextMenu}
        onReply={handleContextMenuAction.bind(null, 'reply')}
        onForward={handleContextMenuAction.bind(null, 'forward')}
        onStar={handleContextMenuAction.bind(null, 'star')}
        onPin={handleContextMenuAction.bind(null, 'pin')}
        onAddToFavorites={handleContextMenuAction.bind(null, 'favorite')}
        onDelete={handleContextMenuAction.bind(null, 'delete')}
        onSelect={handleContextMenuAction.bind(null, 'select')}
        onShare={handleContextMenuAction.bind(null, 'share')}
        onReact={handleContextMenuAction.bind(null, 'react')}
      />

      {/* Enhanced Offline Indicator */}
      <OfflineIndicator 
        isOnline={isOnline} 
        offlineQueue={offlineQueue} 
        isDarkMode={isDarkMode} 
      />

      {/* 🚀 CONCURRENCY INDICATOR: Show when heavy updates are pending */}
      {isPending && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-blue-500 text-white px-2 py-1 rounded text-xs animate-pulse">
            Updating...
          </div>
        </div>
      )}
      
      {/* ⚡️ PERFORMANCE OPTIMIZATION: Use memoized InputArea component */}
      {conversation && (
        <InputArea
          user={user}
          conversation={conversation}
          onSendMessage={handleSendMessage}
          onFileUpload={(file) => fileHandling.handleFileUpload(file, {
            user,
            conversation,
            dispatch,
            chatCache,
            handleSendFileMessage: (url, type) => fileHandling.handleSendFileMessage(url, type, {
              user,
              conversation,
              isAdminDashboard,
              firestoreConvId,
              dispatch,
              pendingMessages
            })
          })}
          sending={sending}
          uploading={uploading}
          uploadProgress={uploadProgress}
          uploadingFile={uploadingFile}
          isOnline={isOnline}
          offlineQueue={offlineQueue}
          replyToMessage={replyToMessage}
          onCancelReply={handleCancelReply}
        />
      )}
      <ImagePreviewModal
        imageUrl={openedImage}
        onClose={() => dispatch({ type: 'SET_OPENED_IMAGE', payload: null })}
      />
      {/* Product Modal */}
      <Suspense fallback={
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-4">
            <div className="rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      }>
        <ProductModal 
          conversation={conversation}
          isOpen={showProductModal}
          onClose={() => dispatch({ type: 'SET_SHOW_PRODUCT_MODAL', payload: false })}
        />
      </Suspense>
      

      
          {process.env.NODE_ENV === 'development' && (
      <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-2 rounded text-xs z-50">
        <div>Renders: {performanceMonitoring.getPerformanceMetrics().renderCount}</div>
        <div>Avg: {performanceMonitoring.getPerformanceMetrics().averageRenderTime.toFixed(1)}ms</div>
        <div>Messages: {performanceMonitoring.getPerformanceMetrics().messageCount}</div>
        <div>Pending: {pendingMessages.length}</div>
        <div>Dedup: {messageProcessing.getDeduplicationStats().totalEntries}</div>
        <div>Batch: {batchRef.current.length}</div>
        <div>Memory: {(performanceMonitoring.getPerformanceMetrics().messageCount * 0.5).toFixed(1)}KB</div>
        <div>Socket: {isConnected ? '🟢' : '🔴'}</div>
        <div>Inflight: {getConnectionStatus().queueLength || 0}</div>
      </div>
    )}
    </section>
  );
} 
